import type { Exam } from "src/types";

// Editable draft of one GOÄ service line, shared by the invoice page's service editor
// (ExamGroupEditor — rows start "existing") and manual invoice creation (rows are all "new").

export type GoaLineStatus = "existing" | "new" | "deleted";

export interface MaterialLineDraft {
    _localId: string;
    kind: string;
    label: string;
    cost: number;
    // PAD-imported lines carry these for display (quantity/unit-price breakdown, see
    // ExamTable.tsx) and sorting (positionsnr — never shown, see ExamTable.tsx); the
    // editor has no UI to change them, but must not silently drop them on save —
    // manually-added lines simply leave them undefined.
    quantity?: number;
    unitPrice?: number;
    positionsnr?: number;
}

export interface GoaLineDraft {
    _localId: string;
    _status: GoaLineStatus;
    id?: string;
    code: string;
    description: string;
    quantity: number;
    price: number;
    factor: number;
    justification: string;
    materialLines: MaterialLineDraft[];
    /** Read-only PAD-imported text, kept separate from the editable `justification` field. */
    annotations: string[];
    /** Precomputed fee override (PAD import only) — mirrors examTotals.ts's lineTotal(),
     *  which takes priority over quantity×price×factor. A flat-fee line's factor is always
     *  0 (parsers.ts hardcodes it — there's no real Steigerungsfaktor for these), so without
     *  this override the amount preview would show 0,00 € for a line that clearly has
     *  a real billed amount. */
    materialCosts?: number;
}

let localIdSeq = 0;
export const nextLocalId = () => `row-${++localIdSeq}`;

export const toDraftRow = (exam: Exam): GoaLineDraft => ({
    _localId: nextLocalId(),
    _status: "existing",
    id: exam.id,
    code: exam.code,
    description: exam.description ?? "",
    quantity: exam.quantity ?? 1,
    price: exam.price ?? 0,
    factor: exam.factor ?? 1,
    justification: exam.justification ?? "",
    materialLines: (exam.materialLines ?? []).map((ml) => ({ _localId: nextLocalId(), kind: ml.kind, label: ml.label, cost: ml.cost, quantity: ml.quantity, unitPrice: ml.unitPrice, positionsnr: ml.positionsnr })),
    annotations: exam.annotations ?? [],
    materialCosts: exam.materialCosts ?? undefined,
});

export const blankDraftRow = (): GoaLineDraft => ({
    _localId: nextLocalId(),
    _status: "new",
    code: "",
    description: "",
    quantity: 1,
    price: 0,
    factor: 1,
    justification: "",
    materialLines: [],
    annotations: [],
});

export const blankMaterialLine = (): MaterialLineDraft => ({ _localId: nextLocalId(), kind: "L", label: "", cost: 0 });

/** Mirrors backend examTotals.ts's lineTotal(): materialCosts (a PAD-import fee override)
 *  takes priority over quantity×price×factor. Material sub-lines are not included. */
export const draftLineAmount = (row: GoaLineDraft) => row.materialCosts || (row.quantity * row.price * row.factor);

export const draftMaterialTotal = (row: GoaLineDraft) => row.materialLines.reduce((s, ml) => s + (ml.cost || 0), 0);

/** Rows still counting towards the invoice (not marked for deletion). */
export const activeRows = (rows: GoaLineDraft[]) => rows.filter((r) => r._status !== "deleted");

/** Same figures the backend stores (examTotals.computeInvoiceTotalsFromExams): `fees` = Invoice.amount,
 *  `material` = Invoice.materialCosts; each summed unrounded, then rounded once to cents. */
export function draftTotals(rows: GoaLineDraft[]): { fees: number; material: number; total: number } {
    const active = activeRows(rows);
    const fees = +active.reduce((s, r) => s + draftLineAmount(r), 0).toFixed(2);
    const material = +active.reduce((s, r) => s + draftMaterialTotal(r), 0).toFixed(2);
    return { fees, material, total: +(fees + material).toFixed(2) };
}
